/**
 * AES-256-GCM 解密，纯 JS 实现，不依赖 WebCrypto。
 *
 * 为什么要自己写：crypto.subtle 只在安全上下文（https 或 localhost）存在。
 * 用局域网 IP 开页面调试、或者部署在纯 http 上时它就是 undefined，
 * 整个玩法页会直接开不出来。报文格式与 WebCrypto 完全一致，
 * sealing.js 优先用 WebCrypto，取不到才落到这里。
 *
 * 只实现解密方向：CTR 与 GHASH 都只用到 AES 的正向分组加密，
 * 所以这里没有逆 S 盒，也没有解密轮函数。
 */

// ── AES 分组加密（只需正向） ──

const SBOX = new Uint8Array(256);
const XTIME = new Uint8Array(256);

(function initTables() {
    // 用 GF(2^8) 上的乘法逆元 + 仿射变换现算 S 盒，省得贴一张 256 项的表
    const exp = new Uint8Array(256);
    const log = new Uint8Array(256);
    let x = 1;
    for (let i = 0; i < 256; i++) {
        exp[i] = x;
        log[x] = i;
        x ^= (x << 1) ^ ((x & 0x80) ? 0x11b : 0); // x *= 3（生成元）
        x &= 0xff;
    }
    for (let i = 0; i < 256; i++) {
        const inv = i === 0 ? 0 : exp[255 - log[i]];
        let s = inv;
        for (let k = 0; k < 4; k++) s ^= ((inv << (k + 1)) | (inv >>> (7 - k))) & 0xff;
        SBOX[i] = s ^ 0x63;
        XTIME[i] = ((i << 1) ^ ((i & 0x80) ? 0x1b : 0)) & 0xff;
    }
})();

/**
 * AES-256 轮密钥扩展。
 * @param {Uint8Array} key 32 字节
 * @returns {Uint8Array} 15 组轮密钥，共 240 字节
 */
function expandKey(key) {
    const Nk = 8, Nr = 14;
    const w = new Uint8Array(16 * (Nr + 1));
    w.set(key);
    let rcon = 1;
    for (let i = Nk; i < 4 * (Nr + 1); i++) {
        const p = (i - 1) * 4;
        let a = w[p], b = w[p + 1], c = w[p + 2], d = w[p + 3];
        if (i % Nk === 0) {
            [a, b, c, d] = [SBOX[b] ^ rcon, SBOX[c], SBOX[d], SBOX[a]];
            rcon = XTIME[rcon];
        } else if (i % Nk === 4) {
            [a, b, c, d] = [SBOX[a], SBOX[b], SBOX[c], SBOX[d]];
        }
        const q = i * 4, r = (i - Nk) * 4;
        w[q] = w[r] ^ a;
        w[q + 1] = w[r + 1] ^ b;
        w[q + 2] = w[r + 2] ^ c;
        w[q + 3] = w[r + 3] ^ d;
    }
    return w;
}

/**
 * 就地加密一个 16 字节分组。
 * @param {Uint8Array} rk 轮密钥
 * @param {Uint8Array} s 16 字节，会被改写
 */
function encryptBlock(rk, s) {
    const Nr = 14;
    for (let i = 0; i < 16; i++) s[i] ^= rk[i];

    for (let round = 1; round <= Nr; round++) {
        for (let i = 0; i < 16; i++) s[i] = SBOX[s[i]];

        // ShiftRows：第 r 行左移 r 格（列主序存储，下标 = 4*col + row）
        let t = s[1]; s[1] = s[5]; s[5] = s[9]; s[9] = s[13]; s[13] = t;
        t = s[2]; s[2] = s[10]; s[10] = t;
        t = s[6]; s[6] = s[14]; s[14] = t;
        t = s[15]; s[15] = s[11]; s[11] = s[7]; s[7] = s[3]; s[3] = t;

        if (round !== Nr) {
            for (let c = 0; c < 16; c += 4) {
                const a0 = s[c], a1 = s[c + 1], a2 = s[c + 2], a3 = s[c + 3];
                const all = a0 ^ a1 ^ a2 ^ a3;
                s[c] ^= all ^ XTIME[a0 ^ a1];
                s[c + 1] ^= all ^ XTIME[a1 ^ a2];
                s[c + 2] ^= all ^ XTIME[a2 ^ a3];
                s[c + 3] ^= all ^ XTIME[a3 ^ a0];
            }
        }

        const off = round * 16;
        for (let i = 0; i < 16; i++) s[i] ^= rk[off + i];
    }
}

// ── GHASH（GF(2^128) 上的乘法，位序按 GCM 规范反着来） ──

/** dst ^= src，16 字节。 */
function xor16(dst, src, srcOff = 0) {
    for (let i = 0; i < 16; i++) dst[i] ^= src[srcOff + i];
}

/**
 * y = y · h，GF(2^128)，就地。
 * @param {Uint8Array} y 16 字节
 * @param {Uint8Array} h 16 字节
 */
function gmul(y, h) {
    const z = new Uint8Array(16);
    const v = h.slice();
    for (let i = 0; i < 128; i++) {
        if ((y[i >> 3] >> (7 - (i & 7))) & 1) xor16(z, v);
        // v >>= 1，最低位被移出时异或 R = 0xE1||0^120
        const lsb = v[15] & 1;
        for (let j = 15; j > 0; j--) v[j] = ((v[j] >>> 1) | ((v[j - 1] & 1) << 7)) & 0xff;
        v[0] >>>= 1;
        if (lsb) v[0] ^= 0xe1;
    }
    y.set(z);
}

/** 把 data 按 16 字节一块喂进 GHASH 状态（不足补零）。 */
function ghashUpdate(state, h, data) {
    for (let off = 0; off < data.length; off += 16) {
        const n = Math.min(16, data.length - off);
        for (let i = 0; i < n; i++) state[i] ^= data[off + i];
        gmul(state, h);
    }
}

/** 64 位大端长度写入。 */
function putLen64(buf, off, bits) {
    for (let i = 7; i >= 0; i--) {
        buf[off + i] = bits & 0xff;
        bits = Math.floor(bits / 256);
    }
}

/**
 * AES-256-GCM 解密并校验 tag。
 * @param {Uint8Array} key 32 字节
 * @param {Uint8Array} iv 12 字节
 * @param {Uint8Array} ciphertext 末尾 16 字节是 tag
 * @param {Uint8Array} aad
 * @returns {Uint8Array} 明文
 * @throws tag 对不上时抛错
 */
export function aesGcmDecrypt(key, iv, ciphertext, aad) {
    if (key.length !== 32) throw new Error('AES-GCM: key must be 32 bytes');
    if (iv.length !== 12) throw new Error('AES-GCM: iv must be 12 bytes');
    if (ciphertext.length < 16) throw new Error('AES-GCM: ciphertext too short');

    const rk = expandKey(key);
    const ct = ciphertext.subarray(0, ciphertext.length - 16);
    const tag = ciphertext.subarray(ciphertext.length - 16);

    // H = E_K(0^128)
    const h = new Uint8Array(16);
    encryptBlock(rk, h);

    // IV 是 12 字节，J0 = IV || 0x00000001
    const j0 = new Uint8Array(16);
    j0.set(iv);
    j0[15] = 1;

    // 认证：GHASH(AAD ‖ pad ‖ C ‖ pad ‖ len(AAD)₆₄ ‖ len(C)₆₄) ⊕ E_K(J0)
    const state = new Uint8Array(16);
    ghashUpdate(state, h, aad);
    ghashUpdate(state, h, ct);
    const lens = new Uint8Array(16);
    putLen64(lens, 0, aad.length * 8);
    putLen64(lens, 8, ct.length * 8);
    ghashUpdate(state, h, lens);

    const ek0 = j0.slice();
    encryptBlock(rk, ek0);
    xor16(state, ek0);

    // 定时无关比较：别按字节短路
    let diff = 0;
    for (let i = 0; i < 16; i++) diff |= state[i] ^ tag[i];
    if (diff !== 0) throw new Error('AES-GCM: authentication failed');

    // 解密：CTR 从 inc32(J0) 起
    const out = new Uint8Array(ct.length);
    const counter = j0.slice();
    const keystream = new Uint8Array(16);
    for (let off = 0; off < ct.length; off += 16) {
        // inc32：只对最后 4 字节做大端自增。
        // 必须写回后再读来判进位 —— `++counter[i]` 的表达式值是 256 而不是回绕后的 0，
        // 用它判断会在第 256 块丢掉进位
        for (let i = 15; i >= 12; i--) {
            counter[i] = (counter[i] + 1) & 0xff;
            if (counter[i] !== 0) break;
        }

        keystream.set(counter);
        encryptBlock(rk, keystream);

        const n = Math.min(16, ct.length - off);
        for (let i = 0; i < n; i++) out[off + i] = ct[off + i] ^ keystream[i];
    }
    return out;
}
